import type { MenuBuilder, MenuItemData } from './types';
/** Meal vs snack for a menu builder section (first item category, API default meal). */
export function isMenuBuilderMealSection(builder: MenuBuilder): boolean {
  const cat = (builder.type_data?.[0]?.food_type_category ?? '').toString().toLowerCase();
  return cat === 'meal';
}

/**
 * Guest main-screen menu only: meals alphabetically, then snacks alphabetically.
 * Subscribed / logged-in menu flows keep API order.
 */
export function sortGuestMainMenuBuilders(builders: MenuBuilder[], locale?: string): MenuBuilder[] {
  if (builders.length <= 1) {
    return builders;
  }
  const label = (b: MenuBuilder) => (b.type_name ?? '').trim().toLowerCase();
  const compare = (a: MenuBuilder, b: MenuBuilder) => label(a).localeCompare(label(b), locale, { sensitivity: 'base' });
  const meals = builders.filter(isMenuBuilderMealSection).sort(compare);
  const snacks = builders.filter(b => !isMenuBuilderMealSection(b)).sort(compare);
  return [...meals, ...snacks];
}

/** Count selected meals vs snacks from builder sections (first item category). */
export function countMealsAndSnacksFromMenuBuilders(
  menu_builders: MenuBuilder[] | undefined,
): { meals: number; snacks: number } {
  let meals = 0;
  let snacks = 0;
  for (const b of menu_builders ?? []) {
    const is_meal = isMenuBuilderMealSection(b);
    const q = b.selected_quantity ?? 0;
    if (is_meal) {
      meals += q;
    } else {
      snacks += q;
    }
  }
  return { meals, snacks };
}

export function menuBuilderMatchesScopedId(builder: MenuBuilder, scoped_id: number): boolean {
  return (
    Number(builder.food_type_id) === scoped_id ||
    Number((builder as { menu_builder_id?: number }).menu_builder_id) === scoped_id ||
    Number((builder as { id?: number }).id) === scoped_id
  );
}

export function findMenuItemByBuilderAndId(builders: MenuBuilder[], menu_builder_id: number, itemId: number): MenuItemData | null {
  const scoped_id = Number(menu_builder_id);
  const item_id_num = Number(itemId);
  const builder = builders.find(b => menuBuilderMatchesScopedId(b, scoped_id));
  if (!builder) {
    return null;
  }
  for (const entry of builder.type_data ?? []) {
    if (Number(entry.id) === item_id_num) {
      return entry;
    }
    for (const r of (entry as { replaceable_menu_items?: MenuItemData[] }).replaceable_menu_items ?? []) {
      if (Number((r as { id?: number }).id) === item_id_num) {
        return r as MenuItemData;
      }
    }
  }
  return null;
}
